import { useState, useEffect, useRef, useCallback } from "react";
import { SECTION_ORDER, SECTION_LABELS } from "../lib/shoppingList";
import RecipeImage from "./RecipeImage";

const NUTRITION_ITEMS = [
  { key: "calories", label: "Calories", unit: "" },
  { key: "protein_g", label: "Protein", unit: "g" },
  { key: "carbs_g", label: "Carbs", unit: "g" },
  { key: "fat_g", label: "Fat", unit: "g" },
  { key: "fiber_g", label: "Fiber", unit: "g" },
];

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function RecipeDetail({ recipe, onClose }) {
  const [visible, setVisible] = useState(false);
  const closeRef = useRef(null);
  const sheetRef = useRef(null);

  // Slide in on mount
  useEffect(() => {
    // Force a layout read before triggering the transition
    sheetRef.current?.getBoundingClientRect();
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Auto-focus close button
  useEffect(() => {
    if (visible) closeRef.current?.focus();
  }, [visible]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Escape key
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = useCallback(() => {
    if (prefersReducedMotion) {
      onClose();
      return;
    }
    setVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  // Group ingredients by section
  const ingredients = Array.isArray(recipe.ingredients)
    ? recipe.ingredients
    : [];
  const groupedIngredients = {};
  ingredients.forEach((ing) => {
    const section = ing.section || "pantry";
    if (!groupedIngredients[section]) groupedIngredients[section] = [];
    groupedIngredients[section].push(ing);
  });
  const orderedSections = SECTION_ORDER.filter(
    (s) => groupedIngredients[s]?.length,
  );

  const steps = Array.isArray(recipe.steps) ? recipe.steps : [];
  const nutrition = recipe.nutrition || {};

  return (
    <div
      className="fixed inset-0 z-50 lg:flex lg:items-center lg:justify-center lg:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={recipe.name}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        style={{
          opacity: visible ? 1 : 0,
          transition: prefersReducedMotion ? "none" : "opacity 0.3s",
        }}
        onClick={handleClose}
      />

      {/* Sheet - bottom on mobile, centered modal on desktop */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-panel rounded-t-2xl overflow-y-auto overscroll-contain lg:relative lg:bottom-auto lg:left-auto lg:right-auto lg:rounded-2xl lg:max-w-2xl lg:w-full lg:max-h-[85vh]"
        style={{
          maxHeight: "85vh",
          transform: visible ? "translateY(0)" : "translateY(100%)",
          transition: prefersReducedMotion
            ? "none"
            : "transform 0.3s ease-out",
        }}
      >
        {/* Drag handle - mobile only */}
        <div className="flex justify-center pt-3 pb-2 sticky top-0 bg-panel z-10 lg:hidden">
          <div className="w-10 h-1 bg-surface rounded-full" />
        </div>

        <div className="px-5 pb-8 lg:p-6 flex flex-col gap-5">
          {/* Hero image \u2014 real photo when available, designed fallback otherwise */}
          <RecipeImage recipe={recipe} className="h-40 rounded-card" />

          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display text-xl font-bold text-ink min-w-0">
              {recipe.name}
            </h2>
            <button
              ref={closeRef}
              type="button"
              onClick={handleClose}
              aria-label="Close"
              className="shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-surface transition-colors text-ink-soft"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-2">
            {recipe.cuisine && (
              <span className="font-body text-xs font-medium px-3 py-1 rounded-full bg-lime/10 text-lime">
                {recipe.cuisine}
              </span>
            )}
            {recipe.category && (
              <span className="font-body text-xs font-medium px-3 py-1 rounded-full bg-mint/10 text-mint capitalize">
                {recipe.category}
              </span>
            )}
            {recipe.servings && (
              <span className="font-body text-xs text-ink-muted">
                {recipe.servings} servings
              </span>
            )}
          </div>

          {/* Time row */}
          {(recipe.prep_time_min != null || recipe.cook_time_min != null) && (
            <div className="flex items-center gap-3 text-xs text-ink-muted font-body">
              <svg
                className="w-4 h-4 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>
              {recipe.prep_time_min != null && (
                <span>{recipe.prep_time_min}m prep</span>
              )}
              {recipe.prep_time_min != null &&
                recipe.cook_time_min != null && (
                  <span className="text-ink-soft">&bull;</span>
                )}
              {recipe.cook_time_min != null && (
                <span>{recipe.cook_time_min}m cook</span>
              )}
            </div>
          )}

          {/* Description */}
          {recipe.description && (
            <p className="font-body text-sm text-ink-soft">
              {recipe.description}
            </p>
          )}

          {/* Ingredients */}
          {orderedSections.length > 0 && (
            <section>
              <h3 className="font-display text-base font-bold text-ink mb-3">
                Ingredients
              </h3>
              <div className="flex flex-col gap-4">
                {orderedSections.map((section) => (
                  <div key={section}>
                    <p className="font-body text-xs font-semibold text-ink-muted mb-1.5">
                      {SECTION_LABELS[section] || section}
                    </p>
                    <ul className="flex flex-col gap-1">
                      {groupedIngredients[section].map((ing, i) => (
                        <li
                          key={i}
                          className="font-body text-sm text-ink-soft flex items-baseline gap-2"
                        >
                          <span className="text-ink-muted shrink-0 w-14 text-right tabular-nums">
                            {ing.amount}
                            {ing.unit ? ` ${ing.unit}` : ""}
                          </span>
                          <span>{ing.item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Steps */}
          {steps.length > 0 && (
            <section>
              <h3 className="font-display text-base font-bold text-ink mb-3">
                Steps
              </h3>
              <ol className="flex flex-col gap-3">
                {steps.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-display text-sm font-bold text-lime shrink-0 w-6 text-right">
                      {i + 1}.
                    </span>
                    <div className="flex flex-col gap-1">
                      <p className="font-body text-sm text-ink-soft">
                        {step.text}
                      </p>
                      {step.time && (
                        <span className="font-body text-xs text-ink-muted bg-surface rounded-full px-2 py-0.5 self-start">
                          {step.time}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Nutrition */}
          {Object.keys(nutrition).length > 0 && (
            <section>
              <h3 className="font-display text-base font-bold text-ink mb-3">
                Nutrition
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {NUTRITION_ITEMS.filter((n) => nutrition[n.key] != null).map(
                  (n) => (
                    <div
                      key={n.key}
                      className="bg-surface rounded-xl px-3 py-2.5 text-center"
                    >
                      <p className="font-display text-lg font-bold text-ink">
                        {nutrition[n.key]}
                        <span className="text-xs font-normal text-ink-muted">
                          {n.unit}
                        </span>
                      </p>
                      <p className="font-body text-xs text-ink-muted">
                        {n.label}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </section>
          )}

          {/* Tags */}
          {recipe.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {recipe.tags.map((tag) => (
                <span
                  key={tag}
                  className="font-body text-xs px-2.5 py-0.5 rounded-full bg-surface text-ink-soft"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Safe area bottom padding */}
          <div className="h-safe-b lg:hidden" />
        </div>
      </div>
    </div>
  );
}
